import { useEffect, useState } from "react";
import Button from "@/components/base/Button";
import AdminPageHeader from "@/pages/admin/components/AdminPageHeader";
import ConfiguracionForm from "./components/ConfiguracionForm";
import { supabase } from "@/lib/supabase";
import { companyInfo } from "@/mocks/site";
import type { CompanyInfo } from "@/types";

const defaultInfo = companyInfo as CompanyInfo;

export default function AdminConfiguracion() {
  const [info, setInfo] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error: queryError } = await supabase
        .from("site_settings")
        .select("valor")
        .eq("clave", "company")
        .maybeSingle();
      if (queryError) throw queryError;
      const stored = (data?.valor ?? {}) as Partial<CompanyInfo>;
      setInfo({ ...defaultInfo, ...stored, redes: stored.redes ?? defaultInfo.redes });
    } catch {
      setError("No pudimos cargar la configuración. Revisa tu conexión e intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleSave = async (value: CompanyInfo) => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      const { error: saveError } = await supabase
        .from("site_settings")
        .upsert(
          { clave: "company", valor: value, actualizado_en: new Date().toISOString() },
          { onConflict: "clave" },
        );
      if (saveError) throw saveError;
      setInfo(value);
      setSaved(true);
    } catch {
      setError("No pudimos guardar la configuración. Intenta nuevamente.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <AdminPageHeader
        title="Configuración general"
        description="Datos de la empresa, contacto, mapa y redes sociales que se muestran en todo el sitio."
        action={
          <Button variant="outline" size="md" icon="ri-refresh-line" onClick={load} disabled={loading}>
            Actualizar
          </Button>
        }
      />

      {saved && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-accent-900">
          <i className="ri-checkbox-circle-line text-base leading-none" aria-hidden="true"></i>
          Configuración guardada correctamente.
        </div>
      )}

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="space-y-4">
          {[0, 1, 2].map((key) => (
            <div key={key} className="h-40 animate-pulse rounded-lg border border-background-200 bg-background-100"></div>
          ))}
        </div>
      ) : info ? (
        <ConfiguracionForm initial={info} saving={saving} onSave={handleSave} />
      ) : null}
    </div>
  );
}